/**
 * O que faz: mostra um recado em português se um pedaço da tela não carregar após o recarregamento.
 * Onde usa: rota /chat e outros carregamentos preguiçosos.
 * Conexões: React Error Boundary.
 * Tabelas: nenhuma.
 * Data: 28/08/2026
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface FalhaCarregamentoChunkProps {
  children: ReactNode;
}

interface FalhaCarregamentoChunkState {
  temErro: boolean;
  ehArquivoAntigo: boolean;
}

function ehFalhaDeArquivoAntigo(erro: Error): boolean {
  // Motivo: só o 404 de JS antigo deve falar em "atualização"; outros crashes têm outra causa.
  const texto = `${erro.name} ${erro.message}`;
  return /Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed|error loading dynamically imported module/i.test(texto);
}

export class FalhaCarregamentoChunk extends Component<
  FalhaCarregamentoChunkProps,
  FalhaCarregamentoChunkState
> {
  state: FalhaCarregamentoChunkState = { temErro: false, ehArquivoAntigo: false };

  static getDerivedStateFromError(erro: Error): FalhaCarregamentoChunkState {
    return { temErro: true, ehArquivoAntigo: ehFalhaDeArquivoAntigo(erro) };
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    console.error('Falha ao carregar um módulo da tela', erro, info);
  }

  render() {
    if (!this.state.temErro) {
      return this.props.children;
    }

    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm font-medium text-slate-800">
          Não foi possível carregar esta tela.
        </p>
        <p className="max-w-sm text-sm text-slate-500">
          {this.state.ehArquivoAntigo
            ? 'Isso costuma acontecer logo depois de uma atualização. Atualize a página para abrir a versão nova.'
            : 'Atualize a página. Se o problema continuar, avise o suporte.'}
        </p>
        <button
          type="button"
          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
          onClick={() => window.location.reload()}
        >
          Atualizar página
        </button>
      </div>
    );
  }
}
